import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import apiClient from '@/services/api';
import { clearStoredAuth, isAccessTokenUsable } from '@/lib/auth-token';

export interface UserProfile {
  _id: string;
  name: string;
  email: string;
  avatar?: string;
  phone?: string;
  wechatOpenid?: string;
  douyinOpenid?: string;
  plan: 'free' | 'pro' | 'max' | 'team';
  membershipExpiresAt?: string;
  credits: number;
  role: 'user' | 'admin';
  provider: string;
  createdAt?: string;
}

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  // 状态
  user: UserProfile | null;
  status: AuthStatus;
  token: string | null;

  // Actions
  fetchProfile: () => Promise<void>;
  setUser: (user: UserProfile | null) => void;
  login: (token: string, user: UserProfile) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
  isAdmin: () => boolean;
  hasPlan: (plan: string) => boolean;
  isPlanExpired: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      status: 'idle',
      token: null,

      fetchProfile: async () => {
        let token = localStorage.getItem('token');
        // 回收抖音/微信移动端整页授权后暂存的 token（无 opener 场景）
        const pending = localStorage.getItem('oauth_pending_token');
        if (!token && pending) {
          token = pending;
          localStorage.setItem('token', token);
          localStorage.removeItem('oauth_pending_token');
        }
        if (!isAccessTokenUsable(token)) {
          clearStoredAuth();
          // SSO：云盘/官网共用登录态——官网未登录时尝试用跨子域 Cookie(aibak_sso) 自动登录
          try {
            const ssoRes = await fetch('/api/auth/sso-exchange', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({}),
              credentials: 'include',
            });
            const ssoData = await ssoRes.json().catch(() => ({}));
            if (ssoRes.ok && ssoData?.token) {
              localStorage.setItem('token', ssoData.token);
              const p2 = await apiClient.get('/auth/profile');
              if (p2?.data) {
                const u = p2.data as UserProfile;
                set({ user: u, token: ssoData.token, status: 'authenticated' });
                localStorage.setItem('user', JSON.stringify(u));
                return;
              }
            }
          } catch { /* 忽略：无 SSO 则按未登录处理 */ }
          set({ user: null, status: 'unauthenticated', token: null });
          return;
        }

        set({ status: 'loading', token });
        try {
          const res: any = await apiClient.get('/auth/profile');
          if (res?.data) {
            const user = res.data as UserProfile;
            set({ user, status: 'authenticated' });
            localStorage.setItem('user', JSON.stringify(user));
          } else if (res?.user) {
            const user = res.user as UserProfile;
            set({ user, status: 'authenticated' });
            localStorage.setItem('user', JSON.stringify(user));
          }
        } catch {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          set({ user: null, status: 'unauthenticated', token: null });
        }
      },

      setUser: (user) => {
        set({ user, status: user ? 'authenticated' : 'unauthenticated' });
        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
        } else {
          localStorage.removeItem('user');
        }
      },

      login: (token, user) => {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        set({ user, token, status: 'authenticated' });
        // 51LA 追踪：登录事件
        import('@/utils/track').then(({ trackEvent, TrackEvents }) => {
          trackEvent(TrackEvents.LOGIN, { method: user?.email ? 'email' : 'unknown' });
        }).catch(() => {});
      },

      logout: () => {
        clearStoredAuth();
        set({ user: null, token: null, status: 'unauthenticated' });
        // 51LA 追踪：登出事件
        import('@/utils/track').then(({ trackEvent, TrackEvents }) => {
          trackEvent(TrackEvents.LOGOUT);
        }).catch(() => {});
      },

      isAuthenticated: () => {
        const state = get();
        const token = state.token || localStorage.getItem('token');
        return state.status === 'authenticated' && !!state.user && isAccessTokenUsable(token);
      },

      isAdmin: () => {
        const state = get();
        return state.user?.role === 'admin';
      },

      hasPlan: (plan: string) => {
        const state = get();
        if (!state.user) return false;
        const plans = ['free', 'pro', 'max', 'team'];
        const userIdx = plans.indexOf(state.user.plan);
        const targetIdx = plans.indexOf(plan);
        return userIdx >= targetIdx;
      },

      isPlanExpired: () => {
        const state = get();
        if (!state.user?.membershipExpiresAt) return false;
        return new Date(state.user.membershipExpiresAt) < new Date();
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        status: state.status,
      }),
    }
  )
);
